import React, { useEffect, useMemo, useState } from 'react';
import { Bell, CheckCheck, Eye, Filter, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { notificationApi } from '@/services/notificationApi';
import { useAuthStore } from '@/stores/authStore';
import { NotificationItem, NotificationReadStatus, NotificationRecipientRole } from '@/types/notification';

const formatDate = (value: string | Date | null | undefined) => {
  if (!value) {
    return '-';
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const scope = isAdmin ? 'admin' : 'vendor';

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const [statusFilter, setStatusFilter] = useState<NotificationReadStatus>('all');
  const [roleFilter, setRoleFilter] = useState<NotificationRecipientRole | 'all'>(isAdmin ? 'admin' : 'vendor');

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.isRead).length,
    [notifications],
  );

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await notificationApi.getNotifications({
        scope,
        status: statusFilter,
        recipientRole: roleFilter,
        limit: 100,
      });
      setNotifications(data);
    } catch (error: any) {
      toast.error(error.message || 'Bildirimler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, [scope, statusFilter, roleFilter]);

  const emitNotificationChange = () => {
    window.dispatchEvent(new Event('notifications:changed'));
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    try {
      if (!notification.isRead) {
        await notificationApi.markAsRead(scope, notification.id);
        setNotifications((current) => current.map((item) => (
          item.id === notification.id
            ? { ...item, isRead: true, readAt: new Date().toISOString() }
            : item
        )));
        emitNotificationChange();
      }

      if (notification.orderId) {
        navigate(isAdmin ? `/admin/orders/${notification.orderId}` : `/vendor/orders/${notification.orderId}`);
      }
    } catch (error: any) {
      toast.error(error.message || 'Bildirim açılamadı');
    }
  };

  const handleMarkAsRead = async (notification: NotificationItem) => {
    try {
      await notificationApi.markAsRead(scope, notification.id);
      setNotifications((current) => current.map((item) => (
        item.id === notification.id
          ? { ...item, isRead: true, readAt: new Date().toISOString() }
          : item
      )));
      emitNotificationChange();
      toast.success('Bildirim okundu olarak işaretlendi');
    } catch (error: any) {
      toast.error(error.message || 'Bildirim güncellenemedi');
    }
  };

  const handleMarkAllAsRead = async () => {
    setIsMarkingAll(true);
    try {
      const updatedCount = await notificationApi.markAllAsRead(scope, roleFilter);
      if (updatedCount > 0) {
        setNotifications((current) => current.map((item) => ({
          ...item,
          isRead: true,
          readAt: item.readAt || new Date().toISOString(),
        })));
        emitNotificationChange();
      }
      toast.success(updatedCount > 0 ? `${updatedCount} bildirim okundu olarak işaretlendi` : 'Okunmamış bildirim yok');
    } catch (error: any) {
      toast.error(error.message || 'Bildirimler güncellenemedi');
    } finally {
      setIsMarkingAll(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-dark dark:text-light">Bildirimler</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {isAdmin ? 'Sistemdeki tüm vendor, kurye ve müşteri bildirimleri' : 'Size ait vendor bildirimleri'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="rounded-full bg-blue-100 px-4 py-2 text-sm font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-200">
            {unreadCount} okunmamış
          </div>
          <button
            onClick={loadNotifications}
            className="btn btn-secondary flex items-center gap-2"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Yenile
          </button>
          <button
            onClick={handleMarkAllAsRead}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
            disabled={isMarkingAll || unreadCount === 0}
          >
            <CheckCheck size={16} />
            Tümünü Okundu Yap
          </button>
        </div>
      </div>

      <div className="card space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
          <Filter size={16} />
          Filtreler
        </div>
        <div className={`grid gap-4 ${isAdmin ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
          <div>
            <label className="mb-2 block text-sm font-medium">Durum</label>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as NotificationReadStatus)}
              className="input-field"
            >
              <option value="all">Tümü</option>
              <option value="unread">Okunmamış</option>
              <option value="read">Okunmuş</option>
            </select>
          </div>

          {isAdmin && (
            <div>
              <label className="mb-2 block text-sm font-medium">Alıcı Tipi</label>
              <select
                value={roleFilter}
                onChange={(event) => setRoleFilter(event.target.value as NotificationRecipientRole | 'all')}
                className="input-field"
              >
                <option value="admin">Admin</option>
                <option value="vendor">Vendor</option>
                <option value="courier">Kurye</option>
                <option value="customer">Müşteri</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin text-4xl">⏳</div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="card py-16 text-center">
          <Bell className="mx-auto mb-4 text-gray-400" size={40} />
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Bildirim bulunamadı</h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Seçili filtrelere uygun kayıt yok.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`card cursor-pointer border transition hover:shadow-md ${notification.isRead ? 'border-gray-200' : 'border-blue-300 bg-blue-50/50 dark:border-blue-700 dark:bg-blue-950/20'}`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${notification.isRead ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'}`}>
                      {notification.isRead ? 'Okundu' : 'Okunmadı'}
                    </span>
                    <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                      {notification.recipientRole === 'admin'
                        ? 'Admin'
                        : notification.recipientRole === 'vendor'
                        ? 'Vendor'
                        : notification.recipientRole === 'courier'
                          ? 'Kurye'
                          : 'Müşteri'}
                    </span>
                    {notification.orderNo && (
                      <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                        #{notification.orderNo}
                      </span>
                    )}
                  </div>

                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{notification.title}</h2>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{notification.body}</p>
                  </div>

                  <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <span>Alıcı: {notification.recipientId}</span>
                    <span>Oluşturulma: {formatDate(notification.createdAt)}</span>
                    <span>Okunma: {formatDate(notification.readAt)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!notification.isRead && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMarkAsRead(notification);
                      }}
                      className="btn btn-secondary flex items-center gap-2"
                    >
                      <Eye size={16} />
                      Okundu Yap
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;